import { AppState, Auth0Provider } from "@auth0/auth0-react";
import React from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth0ClientId } from "src/hooks/useAuth0ClientId";
import { useOrganizationPath } from "src/hooks/useOrganizationPath";
import { saveContext } from "src/scenes/state/localStorage";
import * as Env from "src/services/env";
import { SharedState } from "src/services/sharedState";
import { AUTH, SPECIAL_ROUTES } from "src/services/url/constants";
import {
  EXPLORE_SCHOOLS_TOKEN,
  LOGIN_PREFILL_TOKEN_PARAM,
} from "src/types/searchParams";
import { GenericError } from "../Feedback/GenericError";
import { Loading } from "../Feedback/Loading";
import { NotFound } from "../Feedback/NotFound";
import { RemoteDataView } from "../Layout/RemoteDataView";
import { saveAuthContext } from "./state/localStorage";

type Props = { children: React.ReactNode };

export function Auth0ProviderWithHistory({ children }: Props) {
  const env = Env.read();

  const [searchParams] = useSearchParams();
  const exploreSavedSchoolRefIDs = decodeURIComponent(
    searchParams.get(EXPLORE_SCHOOLS_TOKEN) ?? ""
  )
    .split(",")
    .filter((x) => x);

  if (exploreSavedSchoolRefIDs.length)
    saveContext({ exploreSavedSchoolRefIDs: exploreSavedSchoolRefIDs });

  const onRedirectCallback = React.useCallback((appState?: AppState) => {
    // Prefill token is only for the unauthenticated → Auth0 hop; drop it once signed in.
    const returnTo = appState?.returnTo || window.location.pathname;
    const url = new URL(returnTo, window.location.origin);
    url.searchParams.delete(LOGIN_PREFILL_TOKEN_PARAM);
    SharedState.redirectURL(`${url.pathname}${url.search}`);
  }, []);

  const organization = useOrganizationPath() ?? "";
  const clientIdRD = useAuth0ClientId();

  // Direct fetch to detect invalid organizations
  const [orgValidation, setOrgValidation] = React.useState<{
    isValidating: boolean;
    isInvalid: boolean;
  }>({ isValidating: false, isInvalid: false });

  React.useEffect(() => {
    async function validateOrganization() {
      // Skip validation for special routes and empty organization
      if (!organization || SPECIAL_ROUTES.includes(organization)) {
        setOrgValidation({ isValidating: false, isInvalid: false });
        return;
      }

      // Skip validation for uppercase organization names - let OrganizationUrlRedirect handle the case conversion first
      const hasUppercase = /[A-Z]/.test(organization);
      if (hasUppercase) {
        setOrgValidation({ isValidating: false, isInvalid: false });
        return;
      }

      setOrgValidation({ isValidating: true, isInvalid: false });

      try {
        // Direct fetch to Auth0 config endpoint to check organization validity
        const configUrl = `${env.VITE_CONFIG_URL}/organization/v1/${organization}/Auth0.json`;

        const response = await fetch(configUrl);

        // 403/404 indicates invalid organization
        if (response.status === 403 || response.status === 404) {
          console.log(
            "🚨 Invalid organization detected:",
            organization,
            "Status:",
            response.status
          );
          setOrgValidation({ isValidating: false, isInvalid: true });
        } else if (response.ok) {
          setOrgValidation({ isValidating: false, isInvalid: false });
        } else {
          // Other errors (500, etc.) - don't treat as invalid org
          console.warn(
            "⚠️ Unexpected error validating organization:",
            response.status
          );
          setOrgValidation({ isValidating: false, isInvalid: false });
        }
      } catch (error) {
        console.error("❌ Error validating organization:", error);
        // Network errors - don't treat as invalid org
        setOrgValidation({ isValidating: false, isInvalid: false });
      }
    }

    validateOrganization();
  }, [organization, env.VITE_CONFIG_URL]);

  React.useEffect(() => {
    if (clientIdRD.hasData()) {
      saveAuthContext({
        organization,
        clientId: clientIdRD.data.clientId,
      });
    }
  }, [clientIdRD, organization]);

  // Show 404 for invalid organizations
  if (orgValidation.isInvalid) {
    console.log(
      "🚨 Auth0ProviderWithHistory - SHOWING 404 for invalid organization:",
      organization
    );
    return <NotFound heading="404" text="Organization not found" />;
  }

  // Show loading while validating organization
  if (orgValidation.isValidating) {
    return <Loading />;
  }

  return (
    <RemoteDataView
      remoteData={clientIdRD}
      loading={<Loading />}
      error={(error) => (
        <GenericError
          message="Missing clientId, most likely due to incomplete organization creation."
          error={error}
        />
      )}
    >
      {({ type, clientId }) => {
        return (
          <Auth0Provider
            domain={env.VITE_AUTH0_DOMAIN}
            clientId={clientId}
            redirectUri={
              type === "organization"
                ? `${window.location.origin}/${organization}/${AUTH}`
                : `${window.location.origin}/${AUTH}`
            }
            audience={env.VITE_HASURA_URL}
            onRedirectCallback={onRedirectCallback}
            useRefreshTokens={true} // uses "modern" OAuth technique for keeping a valid JWT
            // setting cacheLocation=localstorage results in fewer calls to OAuth making the apply app faster.
            cacheLocation="localstorage"
          >
            {children}
          </Auth0Provider>
        );
      }}
    </RemoteDataView>
  );
}
